/**
 * Guarded session-storage access for the deploy-recovery guards, which need a
 * marker that survives a reload but not the browsing session.
 *
 * Session storage is unavailable in embedded WebKit contexts and when site data
 * is blocked — the same conditions handled in `./storage`. A missing marker only
 * costs a loop guard, so these degrade quietly rather than fail. Access is also
 * synchronous, which `Storage` is not: a guard has to read its marker before
 * deciding whether to reload.
 */

/**
 * `typeof` must come first: it is the only reference form that tolerates an
 * undeclared identifier, which is how Apple Mail's link preview renderer
 * presents web storage.
 */
const getSessionStorage = (): globalThis.Storage | null => {
  if (typeof sessionStorage === "undefined" || sessionStorage === null) {
    return null;
  }
  return sessionStorage;
};

export const readSessionMarker = (key: string): string | null => {
  try {
    return getSessionStorage()?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

export const writeSessionMarker = (key: string, value: string): void => {
  try {
    getSessionStorage()?.setItem(key, value);
  } catch {
    // Without the marker we lose the loop guard but can still recover once.
  }
};
